import axios, { AxiosError } from 'axios';

import {
  DeclineReservationPayload,
  GetAvailableReservationSlotsResult,
  GetComplexGamesResult,
  GetComplexReservationsResult,
  GetGamesFilters,
  Reservation,
} from '../types/reservations';
import { getHeaders } from './utils/getHeader';
import { Game } from '../types';

type AcceptReservationResultWithError = {
  data?: Reservation;
  error?: any;
};

type DeclineReservationResultWithError = {
  data?: Reservation;
  error?: any;
};

type DeclineGameResultWithError = {
  data?: Game;
  error?: any;
};

type GetComplexReservationsWithError = {
  data?: GetComplexReservationsResult;
  error?: any;
};

type GetComplexGamesWithError = {
  data?: GetComplexGamesResult;
  error?: any;
};

type GetAvailableReservationSlotsResultWithError = {
  data?: GetAvailableReservationSlotsResult;
  error?: any;
};

type GetFieldReservationsResultWithError = {
  data?: Reservation[];
  error?: any;
};

type GetFieldGamesWithReservationsResultWithError = {
  data?: Game[];
  error?: any;
};

export const getComplexReservations = async (
  complexId: number,
  page = 1,
  limit = 50,
  status?: string,
  excludeExpired?: boolean,
  pendingFirst?: boolean
): Promise<GetComplexReservationsWithError> => {
  const url = `${process.env.REACT_APP_API_ENDPOINT}/owner/complexes/${complexId}/reservations?page=${page}&limit=${limit}`;

  let result: GetComplexReservationsWithError = {};

  try {
    const response = await axios.get(url, {
      headers: getHeaders(true),
      params: {
        status,
        excludeExpired,
        pendingFirst,
      },
    });

    result.data = response.data as GetComplexReservationsResult;
  } catch (error) {
    const { response } = error as AxiosError;
    console.error('Error fetching complexes:', error);
    result.error = response?.data;
  }

  return result;
};

export const getComplexGamesWithReservations = async (
  complexId: number,
  page = 1,
  limit = 50,
  status?: string,
  filters: GetGamesFilters = {}
): Promise<GetComplexGamesWithError> => {
  const url = `${process.env.REACT_APP_API_ENDPOINT}/owner/complexes/${complexId}/games?page=${page}&limit=${limit}`;

  let result: GetComplexGamesWithError = {};

  const params = {
    status: status ? status : null,
    fieldId: filters.fieldId ? filters.fieldId : null,
    startDate: filters.startDate ? filters.startDate.toISOString() : null,
    endDate: filters.endDate ? filters.endDate.toISOString() : null,
    upcoming: filters.upcoming ? filters.upcoming : null,
    passed: filters.passed ? filters.passed : null,
    captain: filters.captain ? filters.captain : null,
    team: filters.team ? filters.team : null,
    user: filters.user ? filters.user : null,
    phone: filters.phone ? filters.phone : null,
  };

  try {
    const response = await axios.get(url, {
      headers: getHeaders(true),
      params,
    });

    result.data = response.data as GetComplexGamesResult;
  } catch (error) {
    const { response } = error as AxiosError;
    console.error('Error fetching complexes:', error);
    result.error = response?.data;
  }

  return result;
};

export const acceptReservation = async (
  reservationId: number
): Promise<AcceptReservationResultWithError> => {
  const url = `${process.env.REACT_APP_API_ENDPOINT}/reservations/${reservationId}/approve`;

  let result: AcceptReservationResultWithError = {};

  try {
    const response = await axios.put(
      url,
      {},
      {
        headers: getHeaders(true),
      }
    );

    result.data = response.data as Reservation;
  } catch (error) {
    const { response } = error as AxiosError;
    console.error('Error calling: ', url);
    result.error = response?.data;
  }

  return result;
};

export const declineReservation = async (
  reservationId: number,
  payload: DeclineReservationPayload
): Promise<DeclineReservationResultWithError> => {
  const url = `${process.env.REACT_APP_API_ENDPOINT}/reservations/${reservationId}/decline`;

  let result: DeclineReservationResultWithError = {};

  try {
    const response = await axios.put(url, payload, {
      headers: getHeaders(true),
    });

    result.data = response.data as Reservation;
  } catch (error) {
    const { response } = error as AxiosError;
    console.error('Error calling: ', url);
    result.error = response?.data;
  }

  return result;
};

export const declineSingleGame = async (
  reservationId: number,
  gameId: number,
  payload: DeclineReservationPayload
): Promise<DeclineGameResultWithError> => {
  const url = `${process.env.REACT_APP_API_ENDPOINT}/owner/reservations/${reservationId}/games/${gameId}/decline`;

  let result: DeclineGameResultWithError = {};

  try {
    const response = await axios.put(url, payload, {
      headers: getHeaders(true),
    });

    result.data = response.data as Game;
  } catch (error) {
    const { response } = error as AxiosError;
    console.error('Error calling: ', url);
    result.error = response?.data;
  }

  return result;
};

export const fetchAvailableReservationSlots = async (
  fieldId: number,
  month: number,
  year: number
): Promise<GetAvailableReservationSlotsResultWithError> => {
  const url = `${process.env.REACT_APP_API_ENDPOINT}/reservations/available-dates?fieldId=${fieldId}&month=${month}&year=${year}`;

  let result: GetAvailableReservationSlotsResultWithError = {};

  try {
    const response = await axios.get(url, {
      headers: getHeaders(true),
    });

    result.data = response.data as GetAvailableReservationSlotsResult;
  } catch (error) {
    const { response } = error as AxiosError;
    console.error('Error fetching reservation slots:', error);
    result.error = response?.data;
  }

  return result;
};

export const getReservationsByDatePeriod = async (
  complexId: number,
  fieldId: number,
  startDate: Date,
  endDate: Date
): Promise<GetFieldReservationsResultWithError> => {
  const url = `${
    process.env.REACT_APP_API_ENDPOINT
  }/owner/complexes/${complexId}/fields/${fieldId}/reservations?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;

  let result: GetFieldReservationsResultWithError = {};

  try {
    const response = await axios.get(url, { headers: getHeaders(true) });

    result.data = response.data as Reservation[];
  } catch (error) {
    const { response } = error as AxiosError;
    console.error('Error fetching fields reservations:', error);
    result.error = response?.data;
  }

  return result;
};

export const getFieldGamesWithReservationsByDatePeriod = async (
  complexId: number,
  fieldId: number,
  startDate: Date,
  endDate: Date
): Promise<GetFieldGamesWithReservationsResultWithError> => {
  const url = `${
    process.env.REACT_APP_API_ENDPOINT
  }/owner/complexes/${complexId}/fields/${fieldId}/games?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;

  let result: GetFieldGamesWithReservationsResultWithError = {};

  try {
    const response = await axios.get(url, { headers: getHeaders(true) });

    result.data = response.data as Game[];
  } catch (error) {
    const { response } = error as AxiosError;
    console.error('Error fetching fields reservations:', error);
    result.error = response?.data;
  }

  return result;
};

const ALL_GAMES_PAGE_LIMIT = 100;
const ALL_GAMES_MAX_PAGES = 50;

type GetAllComplexGamesResultWithError = {
  data?: Game[];
  error?: any;
};

/**
 * Fetches every game of a complex (optionally a single field) in the given
 * period by walking through all result pages.
 */
export const getAllComplexGames = async (
  complexId: number,
  filters: Pick<GetGamesFilters, 'fieldId' | 'startDate' | 'endDate'>
): Promise<GetAllComplexGamesResultWithError> => {
  const games: Game[] = [];

  for (let page = 1; page <= ALL_GAMES_MAX_PAGES; page += 1) {
    const { data, error } = await getComplexGamesWithReservations(
      complexId,
      page,
      ALL_GAMES_PAGE_LIMIT,
      undefined,
      filters
    );

    if (error || !data) {
      return { error };
    }

    games.push(...data.games);

    if (!data.hasNext) {
      break;
    }
  }

  return { data: games };
};
